import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { fetchClosurePipelineCriticalTrackingData } from '../../../../redux/prospect-reducers/prospectSlice';
import ReactVortoxSpinner from '../../../common-components/ReactVortoxSpinner';
import PaymentLinkTable from '../../../payment-link-system/PaymentLinkTable';
import { formatDateTime } from '../../../common-data/Converts';
import CmnFilters from '../../common/CmnFilters';

const ClosureExpectedTracker = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const { closurePipelineCriticalTracking, closurePipelineCriticalTrackingLoading  } = useSelector((state) => state?.prospects);

  // Pagination and Filters state
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isMarketing = userInfoData?.account_type === "marketing";
  const isClusterManager = userInfoData?.account_type === "cluster_manager";

  // Initial filter states
  const iniAllFilters = {
    selectedGym:
      !isAdmin && !isMarketing && !isClusterManager
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    selectedLeadStage: null,
  };

  const [allFilters, setAllFilters] = useState(iniAllFilters);

  // API filters state
  const iniFilters = {
    page: isPage + 1,
    limit: pageLimit || 10,
    gym_id: iniAllFilters.selectedGym?.value || "",
    lead_stage: iniAllFilters.selectedLeadStage?.value || "",
  };

  const [filters, setFilters] = useState(iniFilters);

  // Filter functions
  const onResetFilters = () => {
    setAllFilters(iniAllFilters);
    setIsPage(0);
  };

  const onClickSearch = () => {
    setFilters({
      ...filters,
      gym_id: allFilters.selectedGym?.value || "",
      lead_stage: allFilters.selectedLeadStage?.value || "",
      page: 1,
      limit: pageLimit,
    });
    setIsPage(0);
  };

  // Sync filters when allFilters change
  useEffect(() => {
    setFilters({
      page: isPage + 1,
      limit: pageLimit || 10,
      gym_id: allFilters.selectedGym?.value || "",
      lead_stage: allFilters.selectedLeadStage?.value || "",
    });
  }, [
    isPage,
    pageLimit,
    allFilters,
  ]);

  // Sync gym_id when userInfoData changes
  // useEffect(() => {
  //   setAllFilters((prevs) => ({
  //     ...prevs,
  //     selectedGym:
  //       !isAdmin && !isMarketing && !isClusterManager
  //         ? {
  //             label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
  //             value:
  //               userInfoData?.gym_id ||
  //               userInfoData?.user_id ||
  //               userInfoData?.uid ||
  //               "",
  //           }
  //         : "",
  //   }));
  // }, [
  //   userInfoData?.gym_id,
  //   isAdmin,
  //   isMarketing,
  //   isClusterManager,
  //   isGym,
  //   userInfoData?.name,
  //   userInfoData?.gym_name,
  //   userInfoData?.user_id,
  //   userInfoData?.uid,
  // ]);

  useEffect(() => {
    if (!userInfoData?.token) return;
    dispatch(
      fetchClosurePipelineCriticalTrackingData({
        filters,
        token: userInfoData?.token,
      })
    );
  }, [dispatch, userInfoData?.token, filters]);

  // Transform pagination data for PaginationBar component
  const paginationArray = closurePipelineCriticalTracking?.status ? [
    {
      pagination: Number(closurePipelineCriticalTracking?.total) || 0,
      pages: Number(closurePipelineCriticalTracking?.totalPages) || Math.max(1, Math.ceil((Number(closurePipelineCriticalTracking?.total) || 0) / (Number(filters?.limit) || Number(pageLimit) || 10))),
    },
  ] : [];


  const getOverdueColor = (dueStatus) => {
    if (dueStatus?.includes("days overdue")) {
      const days = parseInt(dueStatus.match(/(\d+)/)?.[1] || "0");
      if (days > 3) return "lms-closure-pipeline-tracker-overdue-critical";
      if (days > 0) return "lms-closure-pipeline-tracker-overdue-warning";
    }
    return "lms-closure-pipeline-tracker-overdue-ok";
  };

  // Define table columns
  const tableColumns = [
    {
      dataField: "customer_name",
      text: "Member",
      formatter: (cell, row) => (
        <div>
          <div className="fw-medium">{cell || "Unknown"}</div>
          <div className="text-muted minWidth150">ID: {row?.lead_id || "N/A"}</div>
          <div className="text-muted">Mobile: {row?.mobile || "N/A"}</div>
        </div>
      ),
    },
    {
      dataField: "mem_lead_stage",
      text: "Stage",
      formatter: (cell) => {
        const stage = cell || "Unknown";
        const getStagePillClass = (stageText) => {
          const stage = stageText.toLowerCase();
          if (stage.includes("fresh")) {
            return "stage-pill stage-pill-fresh";
          } else if (stage.includes("renewal")) {
            return "stage-pill stage-pill-renewal";
          } else if (stage.includes("unpaid")) {
            return "stage-pill stage-pill-unpaid";
          } else if (stage.includes("subscribed")) {
            return "stage-pill stage-pill-subscribed";
          } else {
            return "stage-pill stage-pill-default";
          }
        };
        return <span className={getStagePillClass(stage)}>{stage}</span>;
      },
    },
    {
      dataField: "gym_name",
      text: "Gym",
      formatter: (cell) => <span className="minWidth150">{cell || "Unknown"}</span>,
    },
    {
      dataField: "agent_name",
      text: "Agent",
      formatter: (cell) => <span className="minWidth100">{cell || "Unassigned"}</span>,
    },
    {
      dataField: "expected_closure_date",
      text: "Expected Closure",
      formatter: (cell) => <span className="minWidth160">{formatDateTime(cell) || "Not Set"}</span>,
    },
    {
      dataField: "due_status",
      text: "Status",
      formatter: (cell) => (
        <span className={`lms-closure-pipeline-tracker-closure-item-status ${getOverdueColor(cell)}`}>
          {cell || "Status unknown"}
        </span>
      ),
    },
    {
      dataField: "expected_revenue",
      text: "Expected Revenue",
      formatter: (cell) => <span className="minWidth100">₹ {cell || "0"}</span>,
    },
  ];


  return (
    <div className="lms-closure-pipeline-tracker-closure-expected-card">
      <div className="lms-closure-pipeline-tracker-closure-expected-header">
        <h3 className="lms-closure-pipeline-tracker-closure-expected-title">Closure Expected - Critical Tracking</h3>
      </div>
      
      <Row>
        <Col>
          <CmnFilters
            from="closure-expected-tracker"
            userInfoData={userInfoData}
            onClickReset={onResetFilters}
            filters={allFilters}
            setFilters={setAllFilters}
            onClickSearch={onClickSearch}
            searchFilterApplied={
              (allFilters.selectedGym?.value !== "" && allFilters.selectedGym?.value !== iniAllFilters.selectedGym?.value) ||
              (allFilters.selectedLeadStage?.value !== "" && allFilters.selectedLeadStage?.value !== null)
            }
          />
        </Col>
      </Row>
      
      {closurePipelineCriticalTrackingLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}>
          <ReactVortoxSpinner />
        </div>
      ) : (
        <div className="fullWidthFlexCenter overflowAuto separatedTable">
          <PaymentLinkTable
            tableData={closurePipelineCriticalTracking?.data || []}
            tableColumns={tableColumns}
            tablePagination={paginationArray}
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
          />
        </div>
      )}
    </div>
  );
};

export default ClosureExpectedTracker; 